/**
 * Comparison form for artist names.
 *
 * For matching a name nobody typed carefully against the programme. Two callers reach
 * it from opposite ends: `domain/broadcast.ts` folds a pasted schedule, `domain/search.ts`
 * folds a typed query. Both compare against the same programme names, so they have to
 * fold identically — two folders that drifted would produce a bug that reads as two
 * unrelated ones, one in import and one in search.
 *
 * Deliberately not used by `domain/normalize.ts`. That module folds the *source*
 * programme into the committed snapshot — times, Portable Text, duplicate slugs — and
 * carries no character folding at all. There is nothing there to reuse, and a helper
 * spanning both would tie a fetch-time validator to a name matcher.
 */

/** The combining diacritical marks NFD splits off. */
const COMBINING_MARKS = /[\u0300-\u036f]/g

/**
 * Letters that Unicode decomposition cannot reach.
 *
 * `ø` and `æ` are letters in their own right rather than accented forms, so NFD never
 * touches them. They still have to fold: a name written `Gjenfodt Kultur` anywhere
 * outside a Norwegian keyboard has to reach `Gjenfødt Kultur` in the programme.
 *
 * The sigmas are here for a different reason. The 2026 lineup bills Σtella with a
 * Greek capital sigma standing in for a Latin S, while the official programme spells
 * the same artist `Stella` — so without this, importing a Broadcast schedule that
 * contains her silently drops one concert.
 */
const FOLDED_LETTERS: Record<string, string> = { ø: 'o', æ: 'ae', σ: 's', ς: 's' }

const FOLDED = /[øæσς]/g

/**
 * Case- and accent-insensitive form of a string, for comparison only.
 *
 * NFD splits a precomposed letter into base plus combining mark, and the mark is then
 * dropped: `María` → `maria`, `elina ijäs` → `elina ijas`. That covers precisely the
 * letters a Norwegian keyboard cannot type, which is the case that matters — someone
 * looking for Coco María will type `coco maria`. `å` folds to `a` the same way.
 *
 * Lower-casing happens before the map above, so `Σ` arrives there as `σ`.
 *
 * Both sides of every comparison go through this, so it does not matter which one
 * carries the diacritic.
 */
export function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(FOLDED, (letter) => FOLDED_LETTERS[letter] ?? letter)
    .trim()
}
